// Capacitor native bootstrap — initialises status bar, splash, and back-button
// behaviour when running inside the iOS/Android shell. Safe to import on web
// (all plugins are no-ops outside Capacitor).

import { isNativeApp, getPlatform } from "./platform";

const swallow = (p) => Promise.resolve(p).catch(() => {});

export async function initCapacitor() {
  if (!isNativeApp()) return;
  document.documentElement.classList.add("native-app");

  // Status bar
  try {
    const { StatusBar, Style } = await import("@capacitor/status-bar");
    await swallow(StatusBar.setStyle({ style: Style.Dark }));
    if (getPlatform() === "android") {
      await swallow(StatusBar.setBackgroundColor({ color: "#020617" }));
    }
  } catch { /* plugin missing */ }

  // Splash screen
  try {
    const { SplashScreen } = await import("@capacitor/splash-screen");
    await swallow(SplashScreen.hide());
  } catch { /* ignore */ }

  // Hardware back-button
  try {
    const { App } = await import("@capacitor/app");
    swallow(App.addListener("backButton", ({ canGoBack }) => {
      if (canGoBack) window.history.back();
      else swallow(App.exitApp());
    }));
  } catch { /* ignore */ }

  // Keyboard adjustments. The webview itself shrinks when the keyboard opens
  // (Keyboard.resize "native" on iOS, adjustResize on Android); on top of that,
  // keep whichever field has focus scrolled into the visible area, on every screen.
  try {
    const { Keyboard } = await import("@capacitor/keyboard");
    const revealFocused = () => {
      const el = document.activeElement;
      if (el && el !== document.body && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ block: "center", behavior: "smooth" });
      }
    };
    swallow(Keyboard.addListener("keyboardWillShow", (info) => {
      document.body.classList.add("kb-open");
      document.documentElement.style.setProperty("--kb-height", `${info?.keyboardHeight || 0}px`);
    }));
    swallow(Keyboard.addListener("keyboardDidShow", revealFocused));
    swallow(Keyboard.addListener("keyboardWillHide", () => {
      document.body.classList.remove("kb-open");
      document.documentElement.style.setProperty("--kb-height", "0px");
    }));
    // Moving between fields while the keyboard is already open.
    document.addEventListener("focusin", () => {
      if (document.body.classList.contains("kb-open")) setTimeout(revealFocused, 50);
    });
  } catch { /* ignore */ }
}
